import { useCallback, useEffect, useRef, useState } from 'react';
import { Capacitor } from '@capacitor/core';

const PULL_THRESHOLD = 72;   // pull distance (damped px) required to trigger a refresh
const MAX_PULL = 130;        // hard cap on how far the content can be dragged
const REFRESH_HOLD = 100;    // gap the content holds open at while refreshing
const MIN_SPIN_MS = 450;

/** Scroll position of the page — iOS/WKWebView can report this on html or body. */
function getScrollTop() {
  return Math.max(
    window.scrollY || 0,
    document.documentElement.scrollTop || 0,
    document.body.scrollTop || 0
  );
}

/**
 * Opt-out marker for widgets that scroll on their own inside a page that is
 * itself at the top. The hook only ever looked at the PAGE's scroll position,
 * so with the dashboard at the top (where the bell is opened from), a drag
 * down inside the notification panel's list read as a pull: the hook called
 * preventDefault on it, which killed the list's own scroll, and past the
 * threshold it refreshed the whole page behind the panel (owner, native,
 * 2026-10-20; present since the hook landed in July). Touch event targets
 * stay pinned to the element the finger went down on, so closest() on the
 * target answers "did this gesture start inside a marked box", the same
 * mechanism as useSwipeViews' data-swipe-back-opt-out. Portaled children
 * are covered here: the listeners are on document, which real DOM events
 * from a portal still reach.
 */
export const PULL_REFRESH_OPT_OUT_ATTR = 'data-pull-refresh-opt-out';

export function startedInPullRefreshOptOut(target) {
  return !!(target && typeof target.closest === 'function' && target.closest(`[${PULL_REFRESH_OPT_OUT_ATTR}]`));
}

/**
 * Native-only pull-to-refresh. Listeners attach on document (capture phase) so
 * gestures still register when the touch starts on hub chrome, filters, or
 * other controls that might not bubble the same way to window.
 *
 * Requires scrollTop <= 5 at touch start. Vertical-dominant pull only. A
 * touch that starts inside a [data-pull-refresh-opt-out] element never arms
 * the pull (see above).
 */
export function usePullToRefresh(onRefresh, { enabled = true } = {}) {
  const [pullDistance, setPullDistance] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const startYRef = useRef(0);
  const startXRef = useRef(0);
  const activeRef = useRef(false);
  const pullDistanceRef = useRef(0);
  const refreshingRef = useRef(false);
  const onRefreshRef = useRef(onRefresh);

  useEffect(() => {
    onRefreshRef.current = onRefresh;
  }, [onRefresh]);

  const setPull = (value) => {
    pullDistanceRef.current = value;
    setPullDistance(value);
  };

  const runRefresh = useCallback(async () => {
    if (refreshingRef.current || !onRefreshRef.current) return;
    refreshingRef.current = true;
    setRefreshing(true);
    // Hold the content open at a comfortable gap (below the notch) while loading.
    setPull(REFRESH_HOLD);
    const started = Date.now();
    try {
      await onRefreshRef.current();
    } finally {
      const elapsed = Date.now() - started;
      if (elapsed < MIN_SPIN_MS) {
        await new Promise((r) => setTimeout(r, MIN_SPIN_MS - elapsed));
      }
      refreshingRef.current = false;
      setRefreshing(false);
      setPull(0);
      activeRef.current = false;
    }
  }, []);

  useEffect(() => {
    if (!enabled || !Capacitor.isNativePlatform()) return undefined;

    const onTouchStart = (e) => {
      activeRef.current = false;
      if (refreshingRef.current || getScrollTop() > 5) return;
      if (startedInPullRefreshOptOut(e.target)) return;
      const touch = e.touches[0];
      if (!touch) return;
      startYRef.current = touch.clientY;
      startXRef.current = touch.clientX;
      activeRef.current = true;
    };

    const onTouchMove = (e) => {
      if (!activeRef.current || refreshingRef.current) return;
      if (getScrollTop() > 5) {
        activeRef.current = false;
        setPull(0);
        return;
      }
      const touch = e.touches[0];
      if (!touch) return;
      const deltaY = touch.clientY - startYRef.current;
      const deltaX = Math.abs(touch.clientX - startXRef.current);
      if (deltaY <= 0) {
        setPull(0);
        return;
      }
      if (deltaY > 12 && deltaY > deltaX * 1.2) {
        e.preventDefault();
        setPull(Math.min(deltaY * 0.55, MAX_PULL));
      }
    };

    const onTouchEnd = () => {
      if (!activeRef.current || refreshingRef.current) return;
      activeRef.current = false;
      if (pullDistanceRef.current >= PULL_THRESHOLD) {
        runRefresh();
      } else {
        setPull(0);
      }
    };

    const optsCapture = { capture: true };
    document.addEventListener('touchstart', onTouchStart, { passive: true, ...optsCapture });
    document.addEventListener('touchmove', onTouchMove, { passive: false, ...optsCapture });
    document.addEventListener('touchend', onTouchEnd, { passive: true, ...optsCapture });
    document.addEventListener('touchcancel', onTouchEnd, { passive: true, ...optsCapture });

    return () => {
      document.removeEventListener('touchstart', onTouchStart, optsCapture);
      document.removeEventListener('touchmove', onTouchMove, optsCapture);
      document.removeEventListener('touchend', onTouchEnd, optsCapture);
      document.removeEventListener('touchcancel', onTouchEnd, optsCapture);
    };
  }, [enabled, runRefresh]);

  const indicatorOffset = refreshing ? REFRESH_HOLD : pullDistance;
  const showIndicator = indicatorOffset > 0 || refreshing;

  return {
    refreshing,
    pullDistance: indicatorOffset,
    showIndicator,
  };
}

export function PullToRefreshIndicator({ show, offset, refreshing }) {
  if (!show) return null;

  const progress = Math.min(1, offset / PULL_THRESHOLD);

  // The indicator is a top band whose height tracks the pulled-open gap. Content
  // is translated down by the same offset (in App.js), so the spinner sits in the
  // revealed gap just above the page. paddingTop reserves the safe-area (notch /
  // Dynamic Island) so the spinner never lands under the front camera, and
  // flex-end parks it near the content edge.
  return (
    <div
      aria-hidden="true"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        height: `${offset}px`,
        paddingTop: 'env(safe-area-inset-top, 0px)',
        boxSizing: 'border-box',
        zIndex: 10000,
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'center',
        pointerEvents: 'none',
        transition: refreshing ? 'height 0.28s cubic-bezier(0.22, 1, 0.36, 1)' : 'none',
      }}
    >
      <div
        style={{
          marginBottom: 8,
          width: 26,
          height: 26,
          borderRadius: '50%',
          border: '3px solid rgba(77, 182, 172, 0.25)',
          borderTopColor: '#4db6ac',
          opacity: Math.max(0.35, progress),
          animation: refreshing ? 'ptr-spin 0.75s linear infinite' : 'none',
          transform: refreshing ? undefined : `rotate(${progress * 360}deg)`,
        }}
      />
      <style>{`
        @keyframes ptr-spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
